import Vue from 'vue'

import ShowroomModal from './ShowroomModal.vue'
import GoodDeal from './GoodDeal.vue'

/**
 * Renders a virtual DOM element using Vue's render function. It allows dealers define custom buttons without
 * having to override the GoodDeal button styles.
 *
 * This function takes an element object, a render function `h` from Vue, and an `on` object for event listeners.
 * It processes the element's attributes and innerHTML to create a virtual DOM element representation.
 *
 * @param {Object} element - The element to render. It can be an HTMLElement or a plain object containing `tagName`, `attributes`, and `innerHTML`.
 * @param {Function} h - Vue's createElement function. Used to create a virtual DOM element.
 * @param {Object} on - An object containing event listeners to be attached to the element.
 * @returns {Object} A Vue virtual DOM element.
 */
function renderElement (element, h, on) {
  const { tagName, attributes, innerHTML } = element

  const attrs = Array.from(attributes)
    .reduce((result, { nodeName, nodeValue }) => {
      result[nodeName] = nodeValue
      return result
    }, {})

  return h(tagName, {
    attrs,
    on,
    domProps: {
      innerHTML: innerHTML.trim() || undefined
    }
  })
}

async function setup () {
  const startPlugin = () => Array.from(document.querySelectorAll('template[data-m4w]')).map($el => {
    const props = [
      'classList',
      'color',
      'custom',
      'dataset',
      'innerHTML'
    ].reduce((result, key) => {
      result[key] = $el[key] ?? $el.getAttribute(key)
      return result
    }, {})

    const wrapper = document.createElement('section')
    $el.replaceWith(wrapper)
    $el.classList.add('m4w__button')

    const customTrigger = props.custom !== null ? $el.content.children?.[0] : null
    customTrigger?.classList.add('m4w__button')

    if (props.custom !== null && $el.content.children.length > 1) {
      console.error('M4W plugin: Only one element can be added in the first level of a custom template')
    }

    return new Vue({
      data: function () {
        return {
          modalOpened: false,
          buttonToReturn: null,
          showModal: false
        }
      },
      methods: {
        onButtonClick (event) {
          /* DDC fix: make sure we stop event propagation to avoid triggering click events
          in parent elements. Used to happen at: https://www.nissanofclovis.com/used-inventory/index.htm */
          event.stopPropagation()
          event.preventDefault() // In case the element is a link
          this.buttonToReturn = event.currentTarget
          this.showModal = !this.showModal

          setTimeout(() => {
            /* When opening modal, prevents users from seeing larger font sizes flash briefly on
            Dealer.com sites that don't have a root font-size of 16px */
            this.modalOpened = true
          }, 250)
        }
      },
      render (h) {
        return h('section', [
          /* Ensures root font-size is 16px, which isn't always the case for Dealer.com,
          and stops the content behind the dialog modal from scrolling.
          Change #ae_app z-index to prevent it appears over the modal on Roimotors.com */
          h('style', this.modalOpened ? ':root { font-size: 16px !important; overflow: hidden; } #ae_app { z-index: 0 !important }' : ''),
          h(ShowroomModal, {
            props: {
              show: this.showModal,
              inventory: props.dataset.inventory
            },
            on: {
              close: () => {
                this.showModal = false
                this.modalOpened = false

                this.buttonToReturn?.focus()
              }
            }
          }),
          customTrigger
            ? renderElement(customTrigger, h, {
              click: this.onButtonClick
            })
            : h(GoodDeal, {
              class: Array.from(props.classList).map(c => c),
              domProps: { innerHTML: props.innerHTML.trim() || undefined },
              props: {
                cta: props.dataset?.cta,
                color: props.color || 'success',
                ddc: Boolean(window.DDC?.API)
              },
              on: {
                click: this.onButtonClick
              }
            })
        ])
      }
    }).$mount(wrapper)
  })

  // check if we are on a dealer.com site
  if (window.DDC?.API) {
    const { default: dealerDotComSetup } = await import(/* webpackChunkName: "M4W.ddc" */ './plugin.ddc')
    return dealerDotComSetup(window.DDC.API, startPlugin)
  }

  return startPlugin()
}

window.maxsdk.plugin('M4W', setup)

export default setup
